// src/screens/TeamPageScreen.js
// ============================================================================
// PUBLIC TEAM PAGE /t/:teamId (evergreen 3.5b team memberships, design 13.2)
//
// The team's evergreen front door: who is on the roster, whether the squad is
// streaming, the captain's next slot, each member's own channel Join and the
// team's own Join. Open to signed-out visitors (Join -> SignUp).
//
// Data: getTeamPage({teamId}) on focus and on the viewer's team-pair isMember
// false -> true edge (a join clears the Join button without a reload); no
// poll ("Watch the squad" hands off to TeamStream, the live page). The
// viewer's own membership comes from their pair doc (useMyMembership('team',
// teamId)), never from the callable. The page reads no flag: the team Join
// follows the server's membershipsEnabled hint, each roster Join follows that
// member's own channel hint.
//
// Fan copy is money-free: no price, share, percentage or "earn" anywhere on
// this page; the payer sees their own price only inside the Join panel.
// "Join" and "Member", never "Subscribe". Names come from the payload only
// (public_profiles.userName, server-side); the header shows teams.name
// verbatim, sentences use teamDisplayName. The payload carries no invite
// code, and this page renders only the fields it names.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Image,
  ActivityIndicator,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { auth } from '../../firebaseConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import JoinMembershipModal from '../components/JoinMembershipModal';
import MemberPanel, { restoreFocus } from '../components/MemberPanel';
import {
  getTeamPage,
  useMyMembership,
  teamDisplayName,
  formatMembershipDate,
} from '../utils/membershipPayment';

const COLORS = {
  BG: '#06070a',
  CARD: '#0b0b0e',
  CARD_BORDER: '#1f1f25',
  GOLD: '#FCD000',
  TEXT: '#EDEDED',
  DIM: '#8a8a95',
  LIVE: '#e02222',
};

// Below this width the header stacks the logo above the name (13.2 layout).
const HEADER_WRAP_PX = 400;

// The server's name placeholder for a member with no public handle
// (teamStreams.js buildTeamPage). A profile link by that name would not
// resolve, so those rows link by uid.
const NAME_PLACEHOLDER = 'CritCash streamer';

// ---------------------------------------------------------------------------
// Pure helpers (exported for the harness)
// ---------------------------------------------------------------------------

// getTeamPage failure -> 'invalid' (a malformed id: the page can't exist) or
// 'other' (network, internal: worth a retry).
export function teamPageErrorKind(err) {
  const code = String(err?.code || '');
  return code === 'invalid-argument' || code.endsWith('/invalid-argument') ? 'invalid' : 'other';
}

// The team Join and Member predicates (13.2 item 5). Neither while the pair
// hook loads or errs; signed out the hook's constant object lets Join show.
// A disbanding team never shows Join (the server hint is already false).
export function teamPageJoinState({ data, m } = {}) {
  const mm = m || {};
  const showMember = mm.isMember === true;
  const showJoin = !!data
    && data.disbanded !== true
    && data.disbanding !== true
    && data.membershipsEnabled === true
    && data.viewerOnRoster !== true
    && !mm.loading && !mm.error && !mm.isMember;
  return { showJoin, showMember };
}

// A roster row's link label: the visible chips, announced (13.2 item 6).
export function rosterRowLabel(row, squadActive) {
  let label = String(row?.userName || '');
  if (row?.isCaptain === true) label += ', captain';
  if (row?.live === true) label += ', live';
  if (squadActive === true && row?.linked === true) label += ', in the squad';
  return label;
}

// "{Captain} streams next {weekday, time}": the CAPTAIN's personal slot
// (12.2), never a squad plan. '' when there is no slot or no captain row.
export function captainScheduleLine(roster, nextStreamAtMs) {
  const ms = Number(nextStreamAtMs);
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const captain = (Array.isArray(roster) ? roster : []).find((r) => r && r.isCaptain === true);
  const name = captain && typeof captain.userName === 'string' ? captain.userName.trim() : '';
  if (!name) return '';
  const when = new Date(ms).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  return `${name} streams next ${when}`;
}

// The paid-through date a disbanded page states: endsAt, else renewsAt;
// NEVER activeUntil (paid-through + 72 h, three days after the date the
// ending notice states). 0 when neither is known.
export function paidThroughMs(doc) {
  if (!doc) return 0;
  return Number(doc.endsAtMs) || Number(doc.renewsAtMs) || 0;
}

export function howItWorksText(name) {
  return `A team membership is a monthly membership to ${teamDisplayName(name)}. The team's captain chooses its perks, shown before you join. You can cancel anytime in Your memberships.`;
}

// Where a roster row's name link goes (by handle; by uid when the member
// has no public handle).
export function rosterProfileParams(row) {
  const n = typeof row?.userName === 'string' ? row.userName.trim() : '';
  return n && n !== NAME_PLACEHOLDER ? { userName: n } : { uid: row?.uid };
}

const initials = (name) => String(name || '?').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?';

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function TeamPage({ navigation, route }) {
  const teamId = typeof route?.params?.teamId === 'string' ? route.params.teamId : '';
  const isFocused = useIsFocused();
  const { width } = useWindowDimensions();
  const narrow = width > 0 && width < HEADER_WRAP_PX;

  // The viewer's own team pair doc, unconditionally at the top (13.6).
  const m = useMyMembership('team', teamId || null);

  // ONE state per load, tagged with the team it answers for, so a deep link
  // to another team never shows the old page.
  const [page, setPage] = useState({ teamId: null, status: 'loading', data: null, errorKind: null });
  const seqRef = useRef(0);

  // {kind, id, name} | null: the Join modal's target (the team, or one
  // roster member's channel). The last target stays mounted after a close,
  // the 3.5a mount posture; the key tears a payment form down on a switch.
  const [joinOpenFor, setJoinOpenFor] = useState(null);
  const lastJoinRef = useRef(null);
  if (joinOpenFor) lastJoinRef.current = joinOpenFor;
  const [memberPanelOpen, setMemberPanelOpen] = useState(false);

  // Focus return on web (13.13): the element that opened the Join modal,
  // the Member button for the panel.
  const joinOpenerRef = useRef(null);
  const teamJoinBtnRef = useRef(null);
  const memberBtnRef = useRef(null);
  const rowJoinRefs = useRef(new Map());

  const load = useCallback(() => {
    seqRef.current += 1;
    const seq = seqRef.current;
    if (!teamId) {
      setPage({ teamId, status: 'error', data: null, errorKind: 'invalid' });
      return;
    }
    // A refetch keeps the page on screen; a first load (or a retry after an
    // error) shows the spinner.
    setPage((cur) => (cur.teamId === teamId && cur.status === 'ready' && cur.data
      ? cur
      : { teamId, status: 'loading', data: null, errorKind: null }));
    getTeamPage({ teamId })
      .then((res) => {
        if (seqRef.current !== seq) return;
        if (!res || typeof res !== 'object') {
          setPage({ teamId, status: 'error', data: null, errorKind: 'other' });
          return;
        }
        setPage({ teamId, status: 'ready', data: res, errorKind: null });
      })
      .catch((err) => {
        if (seqRef.current !== seq) return;
        console.warn('[TeamPage] getTeamPage failed:', err?.code || '', err?.message || '');
        // A failed refetch keeps the page it already shows.
        setPage((cur) => (cur.teamId === teamId && cur.status === 'ready' && cur.data
          ? cur
          : { teamId, status: 'error', data: null, errorKind: teamPageErrorKind(err) }));
      });
  }, [teamId]);

  // Team switch while mounted: nothing from the old team stays open.
  useEffect(() => {
    setJoinOpenFor(null);
    setMemberPanelOpen(false);
    lastJoinRef.current = null;
  }, [teamId]);

  useEffect(() => {
    if (isFocused) load();
  }, [isFocused, load]);

  // Refetch on the SETTLED non-member -> member edge only (a member opening
  // the page goes loading -> member, which is not a join).
  const pairStateRef = useRef(null); // 'member' | 'non' | null (unknown)
  useEffect(() => { pairStateRef.current = null; }, [teamId]);
  useEffect(() => {
    const now = m.loading || m.error ? null : (m.isMember ? 'member' : 'non');
    const prev = pairStateRef.current;
    if (now !== null) pairStateRef.current = now;
    if (prev === 'non' && now === 'member') load();
  }, [m.loading, m.error, m.isMember, load]);

  const current = page.teamId === teamId ? page : { status: 'loading', data: null, errorKind: null };
  const data = current.status === 'ready' ? current.data : null;
  const { showJoin, showMember } = teamPageJoinState({ data, m });
  const teamNoun = teamDisplayName(data?.name);

  const browseStreams = () => navigation.navigate('Streams');

  const openTeamJoin = () => {
    if (!showJoin || !data) return;
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    joinOpenerRef.current = teamJoinBtnRef.current;
    setJoinOpenFor({ kind: 'team', id: teamId, name: data.name });
  };

  const openChannelJoin = (row) => {
    if (!row || row.membershipsEnabled !== true || !row.uid) return;
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    joinOpenerRef.current = rowJoinRefs.current.get(row.uid) || null;
    setJoinOpenFor({ kind: 'channel', id: row.uid, name: row.userName });
  };

  const closeJoin = useCallback(() => {
    setJoinOpenFor(null);
    restoreFocus(joinOpenerRef);
  }, []);

  const joinTarget = joinOpenFor || lastJoinRef.current;

  // -------------------------------------------------------------------------
  // States
  // -------------------------------------------------------------------------
  const renderLoading = () => (
    <View style={styles.centerBox}>
      <ActivityIndicator size="large" color={COLORS.GOLD} />
    </View>
  );

  const renderError = () => {
    const invalid = current.errorKind === 'invalid';
    return (
      <View style={styles.centerBox}>
        <Ionicons name="people-outline" size={34} color={COLORS.DIM} />
        <Text style={styles.stateTitle}>
          {invalid ? "This team page doesn't exist." : "This team page couldn't load."}
        </Text>
        <View style={styles.stateBtnRow}>
          {!invalid ? (
            <Pressable style={styles.goldBtn} onPress={load} accessibilityRole="button" accessibilityLabel="Try again">
              <Text style={styles.goldBtnText}>Try again</Text>
            </Pressable>
          ) : null}
          <Pressable
            style={invalid ? styles.goldBtn : styles.ghostBtn}
            onPress={browseStreams}
            accessibilityRole="button"
            accessibilityLabel="Browse live streams"
          >
            <Text style={invalid ? styles.goldBtnText : styles.ghostBtnText}>Browse live streams</Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderDisbanded = () => {
    const endDate = m.isMember ? formatMembershipDate(paidThroughMs(m.doc)) : '';
    return (
      <View style={styles.centerBox}>
        <Ionicons name="people-outline" size={34} color={COLORS.DIM} />
        <Text style={styles.stateTitle}>This team has disbanded</Text>
        {m.isMember ? (
          <>
            {endDate ? <Text style={styles.stateSub}>Your membership ends on {endDate}.</Text> : null}
            <Pressable
              style={styles.ghostBtn}
              onPress={() => navigation.navigate('Memberships')}
              accessibilityRole="button"
              accessibilityLabel="Your memberships"
            >
              <Text style={styles.ghostBtnText}>Your memberships</Text>
            </Pressable>
          </>
        ) : null}
        <Pressable style={styles.goldBtn} onPress={browseStreams} accessibilityRole="button" accessibilityLabel="Browse live streams">
          <Text style={styles.goldBtnText}>Browse live streams</Text>
        </Pressable>
      </View>
    );
  };

  const renderAvatar = (row, size) => (
    row.avatarUrl ? (
      <Image source={{ uri: row.avatarUrl }} style={{ width: size, height: size, borderRadius: size / 2 }} />
    ) : (
      <View style={[styles.avatarFallback, { width: size, height: size, borderRadius: size / 2 }]}>
        <Text style={styles.avatarFallbackText}>{initials(row.userName)}</Text>
      </View>
    )
  );

  const renderRosterRow = (row, squadActive) => {
    const inSquad = squadActive && row.linked === true;
    return (
      // A View holding two SIBLING controls (never a button nested in a
      // link): the name link and that member's own channel Join.
      <View key={row.uid} style={styles.rosterRow}>
        <Pressable
          style={styles.rosterLink}
          onPress={() => navigation.navigate('PublicProfile', rosterProfileParams(row))}
          accessibilityRole="link"
          accessibilityLabel={rosterRowLabel(row, squadActive)}
        >
          {renderAvatar(row, 40)}
          <View style={styles.rosterText}>
            <View style={styles.rosterNameRow}>
              <Text style={styles.rosterName} numberOfLines={1}>{row.userName}</Text>
              {row.isCaptain === true ? (
                <View style={styles.captainChip}><Text style={styles.captainChipText}>Captain</Text></View>
              ) : null}
              {row.live === true ? (
                <View style={styles.liveChip}><Text style={styles.liveChipText}>LIVE</Text></View>
              ) : null}
            </View>
            {inSquad ? <Text style={styles.rosterSub}>in the squad</Text> : null}
          </View>
        </Pressable>
        {row.membershipsEnabled === true ? (
          <Pressable
            ref={(el) => {
              if (el) rowJoinRefs.current.set(row.uid, el);
              else rowJoinRefs.current.delete(row.uid);
            }}
            style={styles.rowJoinBtn}
            onPress={() => openChannelJoin(row)}
            accessibilityRole="button"
            accessibilityLabel={`Join ${row.userName}'s channel`}
          >
            <Ionicons name="star-outline" size={12} color={COLORS.GOLD} style={{ marginRight: 4 }} />
            <Text style={styles.rowJoinText}>Join</Text>
          </Pressable>
        ) : null}
      </View>
    );
  };

  const renderPage = () => {
    const roster = Array.isArray(data.roster) ? data.roster.filter((r) => r && r.uid) : [];
    const squad = data.squad && typeof data.squad === 'object' ? data.squad : null;
    const squadActive = squad?.active === true;
    const showLive = squadActive && roster.some((r) => r.live === true);
    const count = Number.isFinite(data.memberCount) ? data.memberCount : roster.length;
    const scheduleLine = captainScheduleLine(roster, data.nextStreamAtMs);
    const captainEntry = !!data.membershipSelf && typeof data.membershipSelf === 'object';
    const chatMembersOnly = data.teamMembersOnlyChatOn === true;

    return (
      <View style={styles.card}>
        {/* Header: the logo (or a people icon), teams.name VERBATIM, the tag,
            the roster count, a LIVE chip while the squad streams. */}
        <View style={styles.hero}>
          <View style={[styles.idRow, narrow && styles.idRowNarrow]}>
            {data.logoUrl ? (
              <Image source={{ uri: data.logoUrl }} style={styles.logo} />
            ) : (
              <View style={[styles.logo, styles.logoFallback]}>
                <Ionicons name="people" size={30} color={COLORS.GOLD} />
              </View>
            )}
            <View style={[styles.idText, narrow && styles.idTextNarrow]}>
              <View style={styles.nameRow}>
                <Text style={styles.teamName}>{data.name || 'A team'}</Text>
                {data.tag ? <Text style={styles.teamTag}>[{data.tag}]</Text> : null}
                {showLive ? (
                  <View style={styles.liveChip}><Text style={styles.liveChipText}>LIVE</Text></View>
                ) : null}
              </View>
              <Text style={styles.meta}>{count} on the roster</Text>
            </View>
          </View>

          {data.disbanding === true ? (
            <View style={styles.warning}>
              <Ionicons name="alert-circle-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
              <Text style={styles.warningText}>
                {Number(data.disbandAtMs) > 0
                  ? (data.viewerOnRoster === true
                    ? `This team is pending disband (due ${formatMembershipDate(data.disbandAtMs)}). Any member may save it.`
                    : `This team is pending disband (due ${formatMembershipDate(data.disbandAtMs)}).`)
                  : (data.viewerOnRoster === true
                    ? 'This team is pending disband. Any member may save it.'
                    : 'This team is pending disband.')}
              </Text>
            </View>
          ) : null}

          {/* Primary row: the live squad, else the captain's next slot, else offline. */}
          <View style={styles.primaryRow}>
            {squadActive && squad.teamStreamId ? (
              <Pressable
                style={styles.goldBtn}
                onPress={() => navigation.navigate('TeamStream', { teamStreamId: squad.teamStreamId })}
                accessibilityRole="button"
                accessibilityLabel="Watch the squad"
              >
                <Text style={styles.goldBtnText}>Watch the squad</Text>
              </Pressable>
            ) : scheduleLine ? (
              <Text style={styles.nextStreamTxt}>{scheduleLine}</Text>
            ) : (
              <Text style={styles.offlineTxt}>Offline right now</Text>
            )}
          </View>

          {/* The team's own Join / Member (gold, the profile page's styling). */}
          {showMember ? (
            <Pressable
              ref={memberBtnRef}
              style={[styles.btn, styles.btnMember]}
              onPress={() => setMemberPanelOpen(true)}
              accessibilityRole="button"
              accessibilityLabel={`Member of ${teamNoun}`}
            >
              <Ionicons name="star" size={12} color={COLORS.GOLD} style={{ marginRight: 5 }} />
              <Text style={styles.btnMemberTxt}>Member</Text>
            </Pressable>
          ) : showJoin ? (
            <Pressable
              ref={teamJoinBtnRef}
              style={[styles.btn, styles.btnJoin]}
              onPress={openTeamJoin}
              accessibilityRole="button"
              accessibilityLabel={`Join ${teamNoun}`}
            >
              <Ionicons name="star-outline" size={12} color={COLORS.GOLD} style={{ marginRight: 5 }} />
              <Text style={styles.btnJoinTxt}>{`Join ${teamNoun}`}</Text>
            </Pressable>
          ) : null}

          {/* The captain's way to the settings (membershipSelf is the
              captain's alone, and only once team memberships can matter). */}
          {captainEntry ? (
            <Pressable
              style={styles.linkRow}
              onPress={() => navigation.navigate('CreatorTeamMemberships', { teamId })}
              accessibilityRole="button"
              accessibilityLabel="Team memberships"
            >
              <Ionicons name="star" size={16} color={COLORS.GOLD} style={{ marginRight: 10 }} />
              <Text style={styles.linkRowText}>Team memberships</Text>
              <Ionicons name="chevron-forward" size={16} color="#666" />
            </Pressable>
          ) : null}
        </View>

        {/* The FULL roster, in the server's order (captain first, then live,
            then uid); the client never re-sorts. */}
        <View style={styles.roster}>
          {roster.map((row) => renderRosterRow(row, squadActive))}
        </View>

        {showJoin || showMember || chatMembersOnly ? (
          <View style={styles.howBox}>
            {showJoin || showMember ? (
              <>
                <Text style={styles.howTitle}>How team memberships work</Text>
                <Text style={styles.howText}>{howItWorksText(data.name)}</Text>
              </>
            ) : null}
            {chatMembersOnly ? (
              <Text style={[styles.howText, (showJoin || showMember) && { marginTop: 8 }]}>
                Squad chat is members-only right now.
              </Text>
            ) : null}
          </View>
        ) : null}
      </View>
    );
  };

  let body;
  if (current.status === 'loading') body = renderLoading();
  else if (current.status === 'error' || !data) body = renderError();
  else if (data.disbanded === true) body = renderDisbanded();
  else body = renderPage();

  return (
    <View style={styles.screen}>
      <Header navigation={navigation} />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        {body}
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />

      {/* Join, mounted once at screen level and keyed by target, so a switch
          between the team and a member's channel tears the form down. */}
      {joinTarget ? (
        <JoinMembershipModal
          key={'join:' + joinTarget.kind + ':' + joinTarget.id}
          target={joinTarget}
          visible={!!joinOpenFor}
          onClose={closeJoin}
          navigation={navigation}
        />
      ) : null}

      <MemberPanel
        visible={memberPanelOpen && showMember}
        onClose={() => setMemberPanelOpen(false)}
        kind="team"
        name={data?.name}
        doc={m.doc}
        navigation={navigation}
        returnFocusRef={memberBtnRef}
      />
    </View>
  );
}

export default TeamPage;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: COLORS.BG },
  scroll: { paddingHorizontal: 16, paddingVertical: 20, alignItems: 'center' },
  card: {
    width: '100%', maxWidth: 720, backgroundColor: COLORS.CARD,
    borderWidth: 1, borderColor: COLORS.CARD_BORDER, borderRadius: 16, overflow: 'hidden',
  },

  // Loading / error / disbanded
  centerBox: { width: '100%', maxWidth: 720, alignItems: 'center', paddingVertical: 60, gap: 10 },
  stateTitle: { color: COLORS.TEXT, fontSize: 16, fontWeight: '700', marginTop: 4, textAlign: 'center' },
  stateSub: { color: '#aaa', fontSize: 13, textAlign: 'center' },
  stateBtnRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 4 },
  goldBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 18,
    minHeight: 36, alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-start',
  },
  goldBtnText: { color: '#0b0b0b', fontSize: 13, fontWeight: '800' },
  ghostBtn: {
    backgroundColor: '#161616', borderWidth: 1, borderColor: '#2a2a2a', borderRadius: 8,
    paddingVertical: 10, paddingHorizontal: 18, minHeight: 36, alignItems: 'center', justifyContent: 'center',
  },
  ghostBtnText: { color: '#ddd', fontSize: 13, fontWeight: '800' },

  // Header
  hero: { padding: 18, borderBottomWidth: 1, borderBottomColor: '#1c1c22', backgroundColor: '#0d0d12' },
  idRow: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  idRowNarrow: { flexDirection: 'column', alignItems: 'flex-start' },
  logo: { width: 64, height: 64, borderRadius: 14, backgroundColor: '#16161c' },
  logoFallback: { alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: 'rgba(252,208,0,0.35)' },
  idText: { flex: 1, minWidth: 0 },
  idTextNarrow: { flex: 0, width: '100%' },
  nameRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  teamName: { fontSize: 22, fontWeight: '900', color: '#fff', flexShrink: 1 },
  teamTag: { fontSize: 13, fontWeight: '800', color: COLORS.GOLD },
  meta: { fontSize: 12.5, color: COLORS.DIM, marginTop: 4 },
  liveChip: { backgroundColor: COLORS.LIVE, borderRadius: 4, paddingVertical: 2, paddingHorizontal: 7 },
  liveChipText: { color: '#fff', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
  warning: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#1a1604',
    borderColor: '#4a3d00', borderWidth: 1, borderRadius: 8,
    paddingVertical: 10, paddingHorizontal: 12, marginTop: 14,
  },
  warningText: { color: '#ddd', fontSize: 12.5, flex: 1, lineHeight: 18 },
  primaryRow: { marginTop: 14 },
  nextStreamTxt: { color: COLORS.GOLD, fontSize: 13, fontWeight: '700' },
  offlineTxt: { color: COLORS.DIM, fontSize: 13, fontWeight: '600' },

  // Team Join / Member (PublicProfileScreen btnJoin / btnMember)
  btn: {
    paddingHorizontal: 16, paddingVertical: 9, borderRadius: 9, minHeight: 36,
    alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-start',
  },
  btnJoin: { flexDirection: 'row', backgroundColor: 'rgba(252,208,0,0.10)', borderWidth: 1, borderColor: COLORS.GOLD, marginTop: 12 },
  btnJoinTxt: { fontSize: 12, fontWeight: '800', color: COLORS.GOLD },
  btnMember: { flexDirection: 'row', backgroundColor: '#161616', borderWidth: 1, borderColor: 'rgba(252,208,0,0.5)', marginTop: 12 },
  btnMemberTxt: { fontSize: 12, fontWeight: '800', color: COLORS.GOLD },

  // The captain's settings row (StreamingAccountsScreen membershipRow)
  linkRow: {
    flexDirection: 'row', alignItems: 'center', minHeight: 44, marginTop: 14,
    paddingVertical: 14, paddingHorizontal: 16, borderRadius: 8, backgroundColor: '#1a1a1a',
  },
  linkRowText: { flex: 1, color: '#FFF', fontSize: 14, fontWeight: '600' },

  // Roster
  roster: { paddingHorizontal: 18, paddingVertical: 6 },
  rosterRow: {
    flexDirection: 'row', alignItems: 'center', paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: '#15151a',
  },
  rosterLink: { flex: 1, minWidth: 0, minHeight: 44, flexDirection: 'row', alignItems: 'center', gap: 12 },
  rosterText: { flex: 1, minWidth: 0 },
  rosterNameRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  rosterName: { color: '#ececf0', fontSize: 14, fontWeight: '700', flexShrink: 1 },
  rosterSub: { color: COLORS.DIM, fontSize: 11.5, marginTop: 2 },
  captainChip: {
    borderWidth: 1, borderColor: 'rgba(252,208,0,0.5)', backgroundColor: 'rgba(252,208,0,0.08)',
    borderRadius: 10, paddingHorizontal: 7, paddingVertical: 1,
  },
  captainChipText: { color: COLORS.GOLD, fontSize: 10, fontWeight: '800' },
  rowJoinBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', minHeight: 36,
    paddingHorizontal: 12, borderRadius: 8, marginLeft: 10,
    backgroundColor: 'rgba(252,208,0,0.10)', borderWidth: 1, borderColor: COLORS.GOLD,
  },
  rowJoinText: { color: COLORS.GOLD, fontSize: 12, fontWeight: '800' },
  avatarFallback: { backgroundColor: '#2a3550', alignItems: 'center', justifyContent: 'center' },
  avatarFallbackText: { color: '#b5d4f4', fontSize: 12, fontWeight: '700' },

  // How team memberships work (money-free)
  howBox: {
    margin: 18, padding: 14, borderRadius: 10,
    backgroundColor: '#101014', borderWidth: 1, borderColor: '#22222a',
  },
  howTitle: { color: '#fff', fontSize: 14, fontWeight: '800', marginBottom: 6 },
  howText: { color: '#c9c9d2', fontSize: 12.5, lineHeight: 18 },
});
